import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { QRCodeCanvas } from 'qrcode.react';
import { useSession } from '../../Session';
import { useFeedback } from '../common/Feedback';
import { usePixPolling } from '../../hooks';
import { useTranslation } from '../../i18n';
import { colors, primaryButtonSx, radius } from '../../theme/tokens';
import { copyToClipboard } from '../../utils/clipboard';
import { ROUTES } from '../../utils/routes';
import type { RechargeResult } from '../../types/api';

/**
 * Modal do PIX: QR Code, copia-e-cola e confirmação automática.
 *
 * É a tela mais crítica do produto. A confirmação NÃO depende de o cliente
 * recarregar nada: enquanto o modal está aberto, `usePixPolling` consulta a API
 * (populada pelo webhook da Safe2Pay) e, assim que o pagamento aparece, a tela
 * confirma sozinha. Cadência (5 s) e limite (10 min) vivem no hook, exatamente
 * como no `startPixPolling` do legado.
 *
 * O componente é montado sob demanda e desmontado ao fechar: o cleanup do
 * efeito encerra o polling, que é o papel do `willClose` do SweetAlert antigo.
 */

interface PixDialogProps {
  data: RechargeResult;
  onClose: () => void;
}

const TITLE_ID = 'pix-dialog-title';
/** Tempo que o botão fica exibindo "Copiado!", igual ao legado. */
const COPIED_FEEDBACK_MS = 2000;

export function PixDialog({ data, onClose }: PixDialogProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { clearSession } = useSession();
  const { show } = useFeedback();
  const { status, start, stop } = usePixPolling();

  const [copied, setCopied] = useState(false);
  const copiedTimeoutRef = useRef<number | null>(null);
  /** Evita disparar o feedback final duas vezes (StrictMode / re-render). */
  const settledRef = useRef(false);

  const copyCode = data.pix_copia_cola ?? '';
  const qrImageUrl = data.pix_QRCode ?? '';

  // Inicia o polling ao abrir e encerra ao desmontar (fechar manualmente).
  useEffect(() => {
    if (data.id === undefined || data.id === null || data.id === '') return undefined;
    start(data.id);
    return () => stop();
  }, [data.id, start, stop]);

  // Desfecho do polling: pago ou tempo esgotado.
  useEffect(() => {
    if (settledRef.current) return;

    if (status === 'paid') {
      settledRef.current = true;
      onClose();
      show({
        kind: 'success',
        title: t.pix.confirmedTitle,
        text: t.pix.confirmedBody,
        confirmLabel: t.common.continue,
        blocking: true,
        onConfirm: () => {
          // A recarga foi feita: a sessão da linha não vale mais nada.
          clearSession();
          navigate(ROUTES.home);
        },
      });
      return;
    }

    if (status === 'timeout') {
      settledRef.current = true;
      onClose();
      show({ kind: 'warning', title: t.pix.timeoutTitle, text: t.pix.timeoutBody });
    }
  }, [status, onClose, show, clearSession, navigate, t]);

  useEffect(
    () => () => {
      if (copiedTimeoutRef.current !== null) window.clearTimeout(copiedTimeoutRef.current);
    },
    []
  );

  const handleCopy = useCallback(async () => {
    const ok = await copyToClipboard(copyCode);
    if (!ok) return;
    setCopied(true);
    if (copiedTimeoutRef.current !== null) window.clearTimeout(copiedTimeoutRef.current);
    copiedTimeoutRef.current = window.setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
  }, [copyCode]);

  return (
    <Dialog
      open
      onClose={onClose}
      aria-labelledby={TITLE_ID}
      maxWidth="xs"
      fullWidth
      slotProps={{ paper: { sx: { borderRadius: radius.lg } } }}
    >
      <DialogTitle
        id={TITLE_ID}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          fontSize: '1.25rem',
          fontWeight: 700,
        }}
      >
        {t.pix.title}
        <IconButton aria-label={t.common.close} onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ textAlign: 'center' }}>
        <Typography sx={{ mb: 3, fontSize: '0.875rem', color: colors.textSecondary }}>
          {t.pix.instructions}
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          {qrImageUrl ? (
            // Quando o ERP já devolve a imagem do QR, ela vence: é a mesma
            // imagem impressa nos demais canais da Contel.
            <Box
              component="img"
              src={qrImageUrl}
              alt={t.pix.qrAlt}
              sx={{ width: 200, height: 200, objectFit: 'contain' }}
            />
          ) : copyCode ? (
            <QRCodeCanvas value={copyCode} size={200} aria-label={t.pix.qrAlt} />
          ) : null}
        </Box>

        {copyCode ? (
          <>
            <Box
              sx={{
                backgroundColor: colors.surfaceMuted,
                borderRadius: radius.sm,
                p: 1.5,
                mb: 2,
                textAlign: 'left',
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                lineHeight: 1.5,
                wordBreak: 'break-all',
                userSelect: 'all',
              }}
            >
              {copyCode}
            </Box>

            <Button
              type="button"
              fullWidth
              variant="contained"
              onClick={handleCopy}
              startIcon={<ContentCopyIcon />}
              sx={{ ...primaryButtonSx, py: 1.25 }}
            >
              <Box component="span" aria-live="polite">
                {copied ? t.common.copied : t.pix.copyCode}
              </Box>
            </Button>
          </>
        ) : null}

        {status === 'polling' ? (
          <Box
            sx={{
              mt: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
            }}
            aria-live="polite"
          >
            <CircularProgress size={14} sx={{ color: colors.textMuted }} />
            <Typography sx={{ fontSize: '0.75rem', color: colors.textMuted }}>
              {t.pix.waiting}
            </Typography>
          </Box>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
